import { useEffect, useState } from 'react';
import { SelectorDeIdioma, useIdioma } from '../idioma';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { CampoContrasena, errorDeClave } from './contrasena';
import { BotonGoogle } from './registro';
import { useVerificacion } from '../componentes/verificacion';

type Enviado = null | 'enlace' | 'recuperar';

/**
 * Entrar: con un enlace al correo (sin contraseña), con Google o, desde el 08-10-2026, con correo y contraseña.
 * «¿Olvidaste tu contraseña?» manda el correo para crear una nueva (/nueva-contrasena).
 */
export function Entrar() {
  const { t } = useIdioma();
  const ir = useNavigate();
  const [consulta] = useSearchParams();
  const [conClave, setConClave] = useState(consulta.get('clave') === '1');
  const [correo, setCorreo] = useState('');
  const [clave, setClave] = useState('');
  const [enviado, setEnviado] = useState<Enviado>(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [faltan, setFaltan] = useState(0);
  const verificacion = useVerificacion();
  const sinVerificar = !verificacion.token;

  useEffect(() => {
    if (!faltan) return;
    const reloj = setTimeout(() => setFaltan((n) => n - 1), 1000);
    return () => clearTimeout(reloj);
  }, [faltan]);

  async function enviarEnlace(e?: React.FormEvent) {
    e?.preventDefault();
    setError('');
    setEnviando(true);
    const { error: fallo } = await supabase.auth.signInWithOtp({
      email: correo.trim(),
      options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/marcas`, captchaToken: verificacion.token },
    });
    verificacion.usado();
    setEnviando(false);
    if (fallo) {
      setError(t(/captcha/i.test(fallo.message) ? errorDeClave(fallo.message) : 'No pudimos enviarte el enlace. Revisa el correo y vuelve a intentarlo.'));
      return;
    }
    setEnviado('enlace');
    setFaltan(60);
  }

  async function entrarConClave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setEnviando(true);
    const { error: fallo } = await supabase.auth.signInWithPassword({ email: correo.trim(), password: clave, options: { captchaToken: verificacion.token } });
    verificacion.usado();
    setEnviando(false);
    if (fallo) return setError(t(errorDeClave(fallo.message)));
    ir('/marcas', { replace: true });
  }

  async function recuperar() {
    if (!correo.includes('@')) return setError(t('Escribe tu correo y vuelve a tocar «¿Olvidaste tu contraseña?».'));
    setError('');
    setEnviando(true);
    const { error: fallo } = await supabase.auth.resetPasswordForEmail(correo.trim(), { redirectTo: `${window.location.origin}/nueva-contrasena`, captchaToken: verificacion.token });
    verificacion.usado();
    setEnviando(false);
    if (fallo) return setError(t(errorDeClave(fallo.message)));
    setEnviado('recuperar');
    setFaltan(60);
  }

  return (
    <main className="portada">
      <div className="idioma-flotante"><SelectorDeIdioma /></div>
      <div className="portada-caja tarjeta">
        {!enviado ? (
          <>
            <h1 className="titulo-portada">{t('Entra con tu correo')}</h1>
            <span className="est-segmentos" style={{ alignSelf: 'flex-start' }}>
              <button type="button" className={!conClave ? 'activo' : ''} onClick={() => { setConClave(false); setError(''); }}>{t('Con un enlace')}</button>
              <button type="button" className={conClave ? 'activo' : ''} onClick={() => { setConClave(true); setError(''); }}>{t('Con contraseña')}</button>
            </span>
            {!conClave ? (
              <>
                <p className="apunte">{t('Sin contraseñas. Te mandamos un enlace y listo.')}</p>
                <form onSubmit={enviarEnlace} className="formulario">
                  <label className="campo">
                    <span>{t('Tu correo')}</span>
                    <input type="email" required autoFocus value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder={t('tunombre@correo.com')} />
                  </label>
                  <button className="btn" disabled={enviando || sinVerificar || !correo.trim()}>
                    {enviando ? t('Enviando…') : t('Enviarme el enlace')}
                  </button>
                </form>
              </>
            ) : (
              <form onSubmit={entrarConClave} className="formulario">
                <label className="campo">
                  <span>{t('Tu correo')}</span>
                  <input type="email" required autoFocus value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder={t('tunombre@correo.com')} autoComplete="email" />
                </label>
                <CampoContrasena etiqueta={t('Tu contraseña')} valor={clave} cambiar={setClave} />
                <button className="btn" disabled={enviando || sinVerificar || !correo.trim() || !clave}>{enviando ? t('Entrando…') : t('Entrar')}</button>
                <button type="button" className="ver-mas" style={{ alignSelf: 'flex-start' }} disabled={enviando || sinVerificar} onClick={() => void recuperar()}>{t('¿Olvidaste tu contraseña?')}</button>
              </form>
            )}
            <div className="col" style={{ gap: 10, marginTop: 14 }}>
              <span className="apunte" style={{ textAlign: 'center' }}>{t('o')}</span>
              <BotonGoogle texto={t('Entrar con Google')} />
              <span className="apunte" style={{ textAlign: 'center' }}>{t('¿Primera vez?')} <Link to="/registro">{t('Crea tu cuenta gratis')}</Link></span>
            </div>
          </>
        ) : (
          <>
            <h1 className="titulo-portada">{t('¡Listo! Revisa tu correo')}</h1>
            <p className="apunte">
              {enviado === 'enlace'
                ? t('Toca el botón del mensaje para entrar. Ábrelo en este mismo dispositivo.')
                : t('Si hay una cuenta con ese correo, te llegó un mensaje para crear una contraseña nueva. El enlace vence en 1 hora.')}
            </p>
            <div className="fila-acciones">
              <button className="btn claro" onClick={() => { setEnviado(null); setCorreo(''); setClave(''); }}>{t('Usar otro correo')}</button>
              <button className="btn claro" disabled={faltan > 0 || sinVerificar} onClick={() => void (enviado === 'enlace' ? enviarEnlace() : recuperar())}>
                {faltan > 0 ? t('Reenviar en {n}s', { n: faltan }) : t('Reenviar')}
              </button>
            </div>
          </>
        )}
        {verificacion.cuadro}
        {verificacion.fallo && <p className="error">{t('No pudimos comprobar que eres una persona. Recarga la página y vuelve a intentarlo.')}</p>}
        {error && <p className="error">{error}</p>}
      </div>
    </main>
  );
}
